import { useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import CustomDataGrid from "../../../components/ui/customDataGrid/customDataGrid.component";
import CustomModal from "../../../components/ui/modal/modal.component";
import JobServicePriceChangeForm from "../../../components/service/jobServicePriceChangeForm.component";
import { useTranslation } from "react-i18next";
import { Build, Edit } from "@mui/icons-material";
import { JOB_SERVICES_ENDPOINT } from "../../../consts";
import { useNavigate } from "react-router-dom";
import { useHasPermission } from "../../../contexts/auth";
import { formatMoney, getContrastColor } from "../../../utils";
import { containsStringFilterOperators } from "../../../components/ui/customDataGrid/filterOperators";

export default function JobServicesCatalogList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewJobServices = useHasPermission("service.view_jobservice");
  const canChangeJobServices = useHasPermission("service.change_jobservice");
  const [priceChangeService, setPriceChangeService] = useState<{
    id: number;
    title: string;
    price: string | null;
  } | null>(null);

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: t("Title"),
      flex: 1,
      minWidth: 160,
      filterOperators: containsStringFilterOperators(),
      renderCell: (params) => {
        const title = params.row.title || "—";
        const color = params.row.color as string | null | undefined;
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Chip
              label={title}
              size="small"
              sx={
                color
                  ? {
                      bgcolor: color,
                      color: getContrastColor(color),
                      "& .MuiChip-label": { color: getContrastColor(color) },
                    }
                  : {
                      bgcolor: "rgba(128, 128, 128, 0.25)",
                      color: "text.secondary",
                    }
              }
            />
          </Box>
        );
      },
    },
    {
      // The catalogue price a finished job is billed at. Not filterable:
      // the endpoint offers no price filter, and a column that silently
      // ignores what the user typed is worse than one that doesn't offer it.
      field: "price",
      headerName: t("Price (net)"),
      type: "number",
      width: 160,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, height: "100%" }}>
          <Typography variant="body2">{formatMoney(params.value)}</Typography>
          {canChangeJobServices && (
            // Clicks and key presses stop here, so changing the price does
            // not also open the service.
            <Box
              component="span"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Tooltip title={t("Change price")}>
                <IconButton
                  size="small"
                  aria-label={t("Change price")}
                  onClick={() =>
                    setPriceChangeService({ id: params.row.id, title: params.row.title, price: params.row.price })
                  }
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: "unit",
      headerName: t("Unit"),
      width: 100,
      filterable: false,
      valueGetter: (value, row) => row.unit || "—",
    },
  ];

  const handleRowClick = (id: string) => {
    navigate(`/service/services-catalog/${id}`);
  };

  if (!canViewJobServices) {
    return null;
  }

  return (
    <>
      <CustomDataGrid
        icon={<Build />}
        title={t("Services Catalog")}
        url={JOB_SERVICES_ENDPOINT}
        gridSettingsKey="JOB_SERVICES_CATALOG"
        columns={columns}
        refreshEventName={["jobServiceCatalogAdded", "jobServiceCatalogUpdated"]}
        onRowClick={handleRowClick}
      />
      {/* Mounted per service, so the form always starts from the price of the
          row it was opened from. */}
      {priceChangeService && (
        <CustomModal
          opened
          setOpened={(opened) => {
            if (!opened) setPriceChangeService(null);
          }}
          title={`${t("Change price")}: ${priceChangeService.title}`}
          width={480}
          minWidth={0}
          minHeight={0}
          content={
            <JobServicePriceChangeForm
              serviceId={priceChangeService.id}
              price={priceChangeService.price}
              onSuccess={() => {
                setPriceChangeService(null);
                window.dispatchEvent(new Event("jobServiceCatalogUpdated"));
              }}
            />
          }
        />
      )}
    </>
  );
}
